import { createEventId, getPageContext, pushDataLayer } from "./push";
import {
    flattenTickets,
    resolveCurrency,
    sumItemsValue,
    ticketToItem,
    tourToItem
} from "./items";

function pushEcommerce(event, ecommerce, prefix) {
    pushDataLayer({ ecommerce: null });
    pushDataLayer({
        event,
        event_id: createEventId(prefix),
        ecommerce
    });
}

export function trackVirtualPageView(overrides = {}) {
    const page = getPageContext();
    pushDataLayer({
        event: "virtual_page_view",
        event_id: createEventId("vpv"),
        page_path: overrides.page_path || page.page_path,
        page_title: overrides.page_title || page.page_title,
        page_type: overrides.page_type || page.page_type,
        template:
            overrides.template != null ? overrides.template : page.template,
        locale: overrides.locale || page.locale
    });
}

/**
 * Intent de saída para o shop — NÃO é add_to_cart Meta standard (evita double).
 * @param {object} input
 * @param {'buy_now'|'add_to_cart'|'buy'|string} [input.intent]
 */
export function trackOutboundBuy(input = {}) {
    const items = (input.items || []).filter(Boolean);
    const currency = resolveCurrency(input.currency);
    const value =
        input.value != null ? Number(input.value) : sumItemsValue(items);

    pushDataLayer({ ecommerce: null });
    pushDataLayer({
        event: "outbound_buy",
        event_id: createEventId("obuy"),
        destination: input.destination || "ticket",
        intent: input.intent || null,
        currency,
        value,
        coupon: input.coupon || null,
        ecommerce: {
            currency,
            value,
            items,
            ...(input.coupon ? { coupon: String(input.coupon) } : {})
        }
    });
}

/**
 * Filtro / mapa / tab de location (interesse em setor).
 * @param {object} input
 * @param {'map'|'type_list'|'dropdown'|'location_tab'|'mobile_filter'} input.filter_source
 * @param {string} [input.location_id] slug estável (grandstands, allocated-chairs, …)
 * @param {string} [input.location_name] label no idioma da página
 * @param {string|number} [input.sector]
 * @param {'select'|'deselect'} [input.filter_action]
 * @param {string} [input.filter_type] dates|types|prices|inclusions|…
 * @param {string|string[]} [input.filter_value]
 */
export function trackFilterTickets(input = {}) {
    pushDataLayer({
        event: "filter_tickets",
        event_id: createEventId("ftkt"),
        filter_source: input.filter_source || null,
        filter_action: input.filter_action || null,
        filter_type: input.filter_type || null,
        filter_value: Array.isArray(input.filter_value)
            ? input.filter_value.join(",")
            : input.filter_value != null
            ? String(input.filter_value)
            : null,
        location_id: input.location_id || null,
        location_name: input.location_name || null,
        sector:
            input.sector != null && input.sector !== ""
                ? String(input.sector)
                : null
    });
}

export function trackSelectPromotion(input = {}) {
    pushDataLayer({
        event: "select_promotion",
        event_id: createEventId("promo"),
        coupon: input.coupon || null,
        promotion_name: input.coupon || null,
        creative_name: input.promo_type || null
    });
}

export function trackCouponDenied(input = {}) {
    pushDataLayer({
        event: "coupon_denied",
        event_id: createEventId("cpnden"),
        coupon: input.coupon || null,
        reason: input.reason || "invalid"
    });
}

export function trackGenerateLead(leadType) {
    pushDataLayer({
        event: "generate_lead",
        event_id: createEventId("lead"),
        lead_type: leadType
    });
}

export function trackSearch(searchTerm, resultCount) {
    pushDataLayer({
        event: "search",
        event_id: createEventId("search"),
        search_term: searchTerm || "",
        ...(resultCount != null ? { result_count: resultCount } : {})
    });
}

/**
 * Download do guia (pós-lead ou clique no PDF).
 * @param {object} [input]
 * @param {string} [input.file_id]
 * @param {string} [input.file_name]
 * @param {string} [input.link_url]
 */
export function trackFileDownload(input = {}) {
    pushDataLayer({
        event: "file_download",
        event_id: createEventId("fdl"),
        file_id: input.file_id || "carnival_guide",
        file_name: input.file_name || null,
        link_url: input.link_url || null
    });
}

/**
 * Saída para o shop sem buy de SKU (cart / orders / account / login).
 * @param {object} input
 * @param {'cart'|'orders'|'account'|'login'|string} input.destination
 */
export function trackOutboundShop(input = {}) {
    pushDataLayer({
        event: "outbound_shop",
        event_id: createEventId("oshop"),
        destination: input.destination || null
    });
}

/**
 * Contato direto (WhatsApp / telefone / e-mail).
 * @param {object} input
 * @param {'whatsapp'|'phone'|'email'|string} input.channel
 */
export function trackOutboundContact(input = {}) {
    pushDataLayer({
        event: "outbound_contact",
        event_id: createEventId("ocontact"),
        channel: input.channel || null
    });
}

/**
 * Interação de conteúdo (sort, load more, modal, splash).
 * @param {object} input
 * @param {string} input.content_type
 * @param {string} [input.content_id]
 */
export function trackSelectContent(input = {}) {
    pushDataLayer({
        event: "select_content",
        event_id: createEventId("selc"),
        content_type: input.content_type || null,
        content_id:
            input.content_id != null ? String(input.content_id) : null
    });
}

export function trackViewItemList(input = {}) {
    const items = (input.items || []).filter(Boolean);
    const currency = resolveCurrency(input.currency);
    pushEcommerce(
        "view_item_list",
        {
            currency,
            item_list_id: input.item_list_id || "tickets",
            item_list_name: input.item_list_name || "Tickets",
            items
        },
        "vil"
    );
}

export function trackViewItem(input = {}) {
    const items = (input.items || []).filter(Boolean);
    const currency = resolveCurrency(input.currency);
    const value =
        input.value != null ? Number(input.value) : sumItemsValue(items);
    pushEcommerce("view_item", { currency, value, items }, "viewitem");
}

export function trackSelectItem(input = {}) {
    const items = (input.items || []).filter(Boolean);
    const currency = resolveCurrency(input.currency);
    pushEcommerce(
        "select_item",
        {
            currency,
            item_list_id: input.item_list_id || "tickets",
            item_list_name: input.item_list_name || "Tickets",
            items
        },
        "selitem"
    );
}

export {
    flattenTickets,
    ticketToItem,
    tourToItem,
    resolveCurrency,
    parseMoney,
    sumItemsValue
} from "./items";
